import { useEffect, useState, useMemo } from "react";
import { BarChart3, Download, Calendar, TrendingUp, MousePointerClick, UserCheck, UserX, Send } from "lucide-react";
import AdminLayout from "@/components/app/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";

const DEFAULT_RANGES = [
  { key: "7", label: "7 ימים אחרונים" },
  { key: "30", label: "30 ימים אחרונים" },
  { key: "90", label: "90 ימים אחרונים" },
  { key: "365", label: "12 חודשים" },
  { key: "all", label: "מכל הזמנים" },
  { key: "custom", label: "טווח מותאם" },
];

function isoDaysAgo(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}
function isoToday() {
  return new Date().toISOString().slice(0, 10);
}

export default function AffiliateReports() {
  const [range, setRange] = useState("30");
  const [dateFrom, setDateFrom] = useState(isoDaysAgo(30));
  const [dateTo, setDateTo] = useState(isoToday());
  const [campaigns, setCampaigns] = useState([]);
  const [campaignId, setCampaignId] = useState("");
  const [groupBy, setGroupBy] = useState("affiliate");
  const [data, setData] = useState({ rows: [], totals: {} });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/campaigns").then((r) => setCampaigns(r.data)).catch(() => {});
  }, []);

  const applyRange = (k) => {
    setRange(k);
    if (k === "all") { setDateFrom(""); setDateTo(""); }
    else if (k === "custom") { /* keep current */ }
    else {
      const days = parseInt(k, 10);
      setDateFrom(isoDaysAgo(days));
      setDateTo(isoToday());
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      if (campaignId && campaignId !== "ALL") params.set("campaign_id", campaignId);
      params.set("group_by", groupBy);
      params.set("include_direct", "true");
      const { data } = await api.get(`/affiliates/report?${params.toString()}`);
      setData(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [dateFrom, dateTo, campaignId, groupBy]); // eslint-disable-line

  const exportCsv = () => {
    if (!data.rows?.length) return;
    const headers = ["affiliate_name", "list_id", "day", "leads", "subscribed_leads", "unsubscribed_leads", "clicks", "ctr", "webhook_ok", "webhook_failed", "webhook_skipped", "top_source"];
    const csv = [
      headers.join(","),
      ...data.rows.map((r) => headers.map((h) => `"${String(r[h] ?? "").replace(/"/g, '""')}"`).join(",")),
    ].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `affiliate_report_${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const t = data.totals || {};
  const conversionRate = useMemo(() => {
    if (!t.leads) return 0;
    return Math.round((t.subscribed_leads / t.leads) * 100);
  }, [t.leads, t.subscribed_leads]);

  return (
    <AdminLayout title="דוחות אפיליאייטים">
      {/* Filter bar */}
      <div className="bg-white rounded-2xl p-4 border border-stone-200 mb-5" data-testid="report-filters">
        <div className="grid md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-3">
            <Label className="text-xs font-bold mb-1.5 block">טווח תאריכים</Label>
            <Select value={range} onValueChange={applyRange}>
              <SelectTrigger className="h-11" data-testid="range-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DEFAULT_RANGES.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {range === "custom" && (
            <>
              <div className="md:col-span-2">
                <Label className="text-xs font-bold mb-1.5 block">מ־</Label>
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-11" data-testid="date-from-input" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs font-bold mb-1.5 block">עד</Label>
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-11" data-testid="date-to-input" />
              </div>
            </>
          )}
          <div className={range === "custom" ? "md:col-span-2" : "md:col-span-3"}>
            <Label className="text-xs font-bold mb-1.5 block">קמפיין</Label>
            <Select value={campaignId || "ALL"} onValueChange={(v) => setCampaignId(v === "ALL" ? "" : v)}>
              <SelectTrigger className="h-11" data-testid="report-campaign-select"><SelectValue placeholder="כל הקמפיינים" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">כל הקמפיינים</SelectItem>
                {campaigns.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.brand_name} (/{c.slug})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs font-bold mb-1.5 block">קיבוץ</Label>
            <Select value={groupBy} onValueChange={setGroupBy}>
              <SelectTrigger className="h-11" data-testid="group-by-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="affiliate">לפי אפיליאייט</SelectItem>
                <SelectItem value="affiliate_and_day">לפי אפיליאייט + יום</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className={range === "custom" ? "md:col-span-1" : "md:col-span-4"}>
            <Button onClick={exportCsv} data-testid="report-export-csv" disabled={!data.rows?.length} className="h-11 rounded-full bg-brand-charcoal hover:bg-stone-800 text-white w-full">
              <Download className="w-4 h-4 me-2" />CSV
            </Button>
          </div>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid md:grid-cols-4 gap-3 mb-5">
        <KpiCard icon={UserCheck} label="לידים" value={t.leads || 0} color="stone" testId="kpi-leads" />
        <KpiCard icon={TrendingUp} label={`מנויים (${conversionRate}%)`} value={t.subscribed_leads || 0} color="emerald" testId="kpi-subscribed" />
        <KpiCard icon={MousePointerClick} label={`קליקים (CTR ${t.ctr || 0}%)`} value={t.clicks || 0} color="amber" testId="kpi-clicks" />
        <KpiCard icon={Send} label={`Webhook OK / Fail`} value={`${t.webhook_ok || 0} / ${t.webhook_failed || 0}`} color="blue" testId="kpi-webhook" />
      </div>

      {/* Table */}
      {loading ? (
        <div className="py-16 text-center text-stone-500">טוען...</div>
      ) : !data.rows?.length ? (
        <div className="bg-white rounded-2xl p-12 border border-dashed border-stone-300 text-center" data-testid="report-empty">
          <BarChart3 className="w-12 h-12 text-stone-300 mx-auto mb-4" />
          <h3 className="font-[800] mb-2">אין נתונים לטווח הזה</h3>
          <p className="text-sm text-stone-500">שנה את הפילטרים או בחר טווח רחב יותר.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden" data-testid="report-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 border-b border-stone-200 text-right">
                <tr>
                  <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider text-stone-500">אפיליאייט</th>
                  {groupBy === "affiliate_and_day" && <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider text-stone-500">יום</th>}
                  <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider text-stone-500 text-center">לידים</th>
                  <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider text-stone-500 text-center">מנויים</th>
                  <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider text-stone-500 text-center">הוסרו</th>
                  <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider text-stone-500 text-center">קליקים</th>
                  <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider text-stone-500 text-center">CTR</th>
                  <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider text-stone-500 text-center">Webhook</th>
                  <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider text-stone-500">Top Source</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r, idx) => (
                  <tr key={`${r.affiliate_id || "direct"}-${r.day || idx}`} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`report-row-${idx}`}>
                    <td className="px-4 py-3 font-bold">
                      {r.affiliate_id ? (
                        <span className="inline-flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-brand-amber" />
                          {r.affiliate_name}
                        </span>
                      ) : (
                        <span className="text-stone-500">{r.affiliate_name}</span>
                      )}
                      {r.list_id && <span className="block text-xs text-stone-400 mt-0.5 ltr">list_id: {r.list_id}</span>}
                    </td>
                    {groupBy === "affiliate_and_day" && <td className="px-4 py-3 text-xs ltr">{r.day}</td>}
                    <td className="px-4 py-3 text-center font-bold">{r.leads}</td>
                    <td className="px-4 py-3 text-center text-emerald-700">{r.subscribed_leads}</td>
                    <td className="px-4 py-3 text-center text-red-600">{r.unsubscribed_leads}</td>
                    <td className="px-4 py-3 text-center">{r.clicks}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${r.ctr >= 20 ? "bg-emerald-100 text-emerald-700" : r.ctr >= 10 ? "bg-amber-100 text-amber-700" : "bg-stone-100 text-stone-600"}`}>
                        {r.ctr}%
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-xs">
                      <span className="text-emerald-700">{r.webhook_ok}</span>
                      {" · "}
                      <span className="text-red-600">{r.webhook_failed}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-stone-600">{r.top_source}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-stone-50 font-bold" data-testid="report-totals">
                <tr>
                  <td className="px-4 py-3">סה״כ</td>
                  {groupBy === "affiliate_and_day" && <td />}
                  <td className="px-4 py-3 text-center">{t.leads || 0}</td>
                  <td className="px-4 py-3 text-center text-emerald-700">{t.subscribed_leads || 0}</td>
                  <td className="px-4 py-3 text-center text-red-600">{t.unsubscribed_leads || 0}</td>
                  <td className="px-4 py-3 text-center">{t.clicks || 0}</td>
                  <td className="px-4 py-3 text-center">{t.ctr || 0}%</td>
                  <td className="px-4 py-3 text-center text-xs">{t.webhook_ok || 0} · {t.webhook_failed || 0}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

function KpiCard({ icon: Icon, label, value, color, testId }) {
  const colors = {
    stone: "bg-stone-50 text-stone-700 border-stone-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-brand-amber border-amber-200",
    blue: "bg-blue-50 text-blue-700 border-blue-200",
  };
  return (
    <div className="bg-white rounded-2xl p-4 border border-stone-200" data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-bold text-stone-500 uppercase tracking-wider">{label}</span>
        <div className={`w-9 h-9 rounded-lg border flex items-center justify-center ${colors[color] || colors.stone}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div className="text-2xl font-[900]">{value}</div>
    </div>
  );
}
